import React, { CSSProperties, useRef } from "react";
import { FiEdit3, FiTrash2, FiShare, FiInfo } from "react-icons/fi";
import { getThemeColors, getComponentColors, Theme } from "../../theme/colors";

interface ChatOptionsMenuProps {
  activeOptions: [string | null, boolean];
  optionsPosition: { top: number; left: number } | null;
  devNumber: number;
  sharedChatList: { id: string }[];
  sharedWithUserChats: { id: string }[];
  handleRename: (chatId: string) => void;
  handleDelete: (chatId: string) => void;
  handleChatShare: (chatId: string) => void;
  handleChatUnShare: (chatId: string) => void;
  handleChatDetails: (
    chatId: string,
    source: "shared-chats" | "shared-with-me"
  ) => void;
  username: string;
  colorMode: string;
}

export const ChatOptionsMenu: React.FC<ChatOptionsMenuProps> = ({
  activeOptions,
  optionsPosition,
  devNumber,
  sharedChatList,
  sharedWithUserChats,
  handleRename,
  handleDelete,
  handleChatShare,
  handleChatUnShare,
  handleChatDetails,
  username,
  colorMode,
}) => {
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const currentColors = getThemeColors(username, colorMode as Theme);
  const sidebarColors = getComponentColors("sidebar", colorMode as Theme);

  const buttonStyle: CSSProperties = {
    borderWidth: "1px",
    borderColor: currentColors.border,
    borderRadius: "4px",
    padding: "0.5rem",
    justifyContent: "flex-start",
    transition: "all ease 0.5s",
    backgroundColor: "transparent",
    cursor: "pointer",
    color: currentColors.text,
    overflow: "hidden",
    whiteSpace: "nowrap",
  };

  const buttonHoverStyle: CSSProperties = {
    backgroundColor: sidebarColors.buttonHover || currentColors.buttonHover,
  };

  const optionsMenuStyle: CSSProperties = {
    position: "absolute",
    backgroundColor: currentColors.optionsBg,
    padding: "0.5rem",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
    zIndex: 1000,
    minWidth: "150px",
  };

  const optionsButtonStyle: CSSProperties = {
    ...buttonStyle,
    width: "100%",
    padding: "0.5rem 1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    border: "none",
    backgroundColor: "transparent",
    transition: "background-color 0.2s ease",
  };

  if (!activeOptions[0] || !optionsPosition) return null;

  return (
    <div
      ref={optionsRef}
      style={{
        ...optionsMenuStyle,
        top: optionsPosition.top,
        left: optionsPosition.left,
      }}
    >
      {!activeOptions[1] ? (
        <>
          <button
            style={optionsButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleRename(activeOptions[0] as string);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                buttonHoverStyle.backgroundColor || "";
            }}
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <FiEdit3 />
            <span>Rename</span>
          </button>
          <button
            style={optionsButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(activeOptions[0] as string);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                buttonHoverStyle.backgroundColor || "";
            }}
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <FiTrash2 />
            <span>Delete</span>
          </button>
          {
            <button
              style={optionsButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleChatShare(activeOptions[0] as string);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  buttonHoverStyle.backgroundColor || "";
              }}
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <FiShare />
              <span>Share</span>
            </button>
          }
        </>
      ) : (
        <>
          {sharedChatList.some((c) => c.id === activeOptions[0]) && (
            <button
              style={optionsButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleChatUnShare(activeOptions[0] as string);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  buttonHoverStyle.backgroundColor || "";
              }}
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <FiShare />
              <span>UnShare</span>
            </button>
          )}
          <button
            style={optionsButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              const isFromSharedWithMe = sharedWithUserChats.some(
                (c) => c.id === activeOptions[0]
              );
              handleChatDetails(
                activeOptions[0] as string,
                isFromSharedWithMe ? "shared-with-me" : "shared-chats"
              );
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                buttonHoverStyle.backgroundColor || "";
            }}
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <FiInfo />
            <span>Details</span>
          </button>
        </>
      )}
    </div>
  );
};
